import { useNavigate, useLocation, useParams } from "react-router-dom";
import { MACHINES_URL } from "@/machines/routes";
import { SETTINGS_URL } from "@/settings/config/settings-urls";
import { useModal } from "@/shared/lib/modal/modal-context";
import { useAutoRefreshStore } from "@/shared/stores/useAutoRefreshStore";
import { useGetMachineById } from "@/machines/api/queries/machines.queries";

export const useTopBarHandlers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { openModal } = useModal();

  // Fetch machine data if on machine detail page
  const { id: machineId } = params;
  const { data: machineData } = useGetMachineById(machineId);

  // Auto-refresh state
  const isLoading = useAutoRefreshStore((s) => s.isLoading);
  const startLoading = useAutoRefreshStore((s) => s.startLoading);
  const stopLoading = useAutoRefreshStore((s) => s.stopLoading);
  const setLastManualRefresh = useAutoRefreshStore((s) => s.setLastManualRefresh);

  const handleImportClick = () => {
    navigate(MACHINES_URL.POLYGONS);
  };

  const handleExportClick = () => {
    // Build export URL with context-aware parameters
    let exportUrl = MACHINES_URL.EXPORT;
    const queryParams = new URLSearchParams();

    // Check if on location detail page: /location/:id
    if (location.pathname.match(/^\/location\/\d+$/)) {
      const locationId = params.id;
      if (locationId) {
        queryParams.set('location', locationId);
      }
    }
    // Check if on machine detail page: /machine/:id or /machine/live/:id
    else if (location.pathname.match(/^\/machine\/(live\/)?\d+$/)) {
      const machine = machineData?.data;
      if (machine) {
        const locationId = machine.location?.id;
        const deviceMxid = machine.device_mxid;

        if (locationId) {
          queryParams.set('location', locationId.toString());
        }
        if (deviceMxid) {
          queryParams.set('machine', deviceMxid);
        }
      }
    }

    // Append query parameters if any
    if (queryParams.toString()) {
      exportUrl = `${exportUrl}?${queryParams.toString()}`;
    }

    navigate(exportUrl);
  };

  const handleSettingsClick = () => {
    // Build settings URL with context-aware scope
    let settingsUrl = SETTINGS_URL.ROOT;

    // Check if on machine detail page: /machine/:id or /machine/live/:id
    if (location.pathname.match(/^\/machine\/(live\/)?\d+$/)) {
      const machine = machineData?.data;
      if (machine?.id) {
        settingsUrl = `${SETTINGS_URL.MACHINE}/${machine.id}`;
      }
    }
    // Check if on location detail page: /location/:id
    else if (location.pathname.match(/^\/location\/\d+$/)) {
      const locationId = params.id;
      if (locationId) {
        settingsUrl = `${SETTINGS_URL.LOCATION}/${locationId}`;
      }
    }
    // Otherwise navigate to organization settings
    else {
      settingsUrl = SETTINGS_URL.ORGANIZATION;
    }

    navigate(settingsUrl);
  };

  const handleDateTimeClick = () => {
    openModal("date-time-range", {});
  };

  const handleAutoRefreshClick = () => {
    openModal("auto-refresh", {});
  };

  const handleRefreshClick = () => {
    if (!isLoading) {
      setLastManualRefresh(Date.now());
      startLoading();
      setTimeout(() => {
        stopLoading();
      }, 1000);
    }
  };

  return {
    handleImportClick,
    handleExportClick,
    handleSettingsClick,
    handleDateTimeClick,
    handleAutoRefreshClick,
    handleRefreshClick,
    isLoading,
  };
};
